import { DateTime } from 'luxon';

import { CompetitorLossFormikType } from '@core/formik/projectOrQuoteCompetitor';
import Enums from '@core/types/api/Enums';
import { Project } from '@core/types/api/Project/Project';
import type { ProjectCompetitor } from '@core/types/api/ProjectCompetitor/ProjectCompetitor';
import { ProjectSummary } from '@core/types/api/ProjectSummary/ProjectSummary';
import type { QuoteCompetitor } from '@core/types/api/QuoteCompetitor/QuoteCompetitor';
import type { LookupInputOption } from '@core/types/app';
import { hasStringValue, hasValue } from '@core/utils/equality';

export const getLossReasonName = (data: CompetitorInfoData): string | null => {
  if (data === null || !('lossReason' in data)) {
    return null;
  }
  return hasValue(data.lossReason.name) ? data.lossReason.name : null;
};

export type CompetitorInfoData = ProjectCompetitor | QuoteCompetitor | null;

export const hasCompetitorLossData = (data: CompetitorInfoData): boolean => {
  if (data === null) {
    return false;
  }

  return (
    hasStringValue(data.competitor?.name ?? null) ||
    hasStringValue(getLossReasonName(data)) ||
    hasStringValue(data.lossReasonNotes) ||
    (data.products ?? []).length > 0
  );
};

export const hasCompetitorLossDataFromFormik = (values: CompetitorLossFormikType): boolean =>
  hasValue(values.competitor?.id) ||
  hasValue(values.lossReason?.id) ||
  hasStringValue(values.lossReasonNotes) ||
  (values.competitorProducts ?? []).some((product) => hasValue(product.product?.id));

// A removed reason is not listed any more, yet the record that recorded it must still show it.
export const getLossReasonOptions = (
  lossReasons: { id: string; name: string }[],
  selected: LookupInputOption | null | undefined,
): LookupInputOption[] => {
  const options = lossReasons.map((reason) => ({ label: reason.name, value: reason.id }));
  if (!hasValue(selected) || options.some((option) => option.value === selected.value)) {
    return options;
  }
  return [...options, selected];
};

export const reprioritizeLossReasons = <T extends { priority: number }>(
  ordered: T[],
): { reprioritized: T[]; moved: T[] } => {
  const reprioritized = ordered.map((reason, index) => ({ ...reason, priority: index + 1 }));
  const moved = reprioritized.filter(
    (reason, index) => reason.priority !== ordered[index].priority,
  );

  return { reprioritized, moved };
};

type LossReasonText = { name: string; description: string };

/** Whether closing would lose a loss reason draft: a new one holding any text, or an edit
 * that differs from the saved reason. */
export const isLossReasonDraftChanged = (
  draft: LossReasonText,
  saved: LossReasonText | null,
): boolean =>
  hasValue(saved)
    ? draft.name !== saved.name || draft.description !== saved.description
    : hasStringValue(draft.name) || hasStringValue(draft.description);

export const getDaysOpen = (project: Project): number => {
  const startDate = DateTime.fromISO(project.createdAt.toISO() ?? '');

  const isFinalStatus = [
    Enums.ProjectStatusSystemBehavior.Lost,
    Enums.ProjectStatusSystemBehavior.Won,
    Enums.ProjectStatusSystemBehavior.Archive,
  ].includes(project.projectStatus.systemBehavior);

  const endDate =
    isFinalStatus && project.lastStatusChange ? project.lastStatusChange : DateTime.now();

  const days = Math.floor(endDate.diff(startDate, 'days').days);

  return days < 0 ? 0 : days;
};
